import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import classes from "./MessageComponent.scss";
import classNames from "classnames";
import { Button, IconButton, TextAreaInput, Tooltip } from "@toddle-edu/ds-web";
import {
  CodeOutlined,
  CopyOutlined,
  ImageSquareOutlined,
  MathematicsOutlined,
  MicrophoneOutlined,
  MinusCircleOutlined,
  PauseFilled,
  PencilLineOutlined,
  TickLargeOutlined,
  UploadOutlined,
  VolumeHighOutlined,
} from "@toddle-edu/ds-icons";
import MarkdownView from "UIComponents/MarkdownView";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import {
  createAIConversationAttachments,
  deleteAIConversationAttachment,
  deleteAIMessage,
  updateAIMessage,
} from "../../modules/GPTPlaygroundModule";
import { copyToClipboard } from "Utils/CopyToClipboard";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { createAttachment, getSpeechToTextTranscript } from "modules/Services";
import { getUserId } from "modules/CommonSelectors";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import CodeEditor from "UIComponents/CodeEditor";
import MathEquationEditor from "UIComponents/MathEquationEditor";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";
import { fontWeight } from "@toddle-edu/ds-theme";
import ClickableHighlightedText from "UIComponents/ClickableHighlightedText";
import { CreateDropDown } from "Attachment/index";
import {
  VALID_FILE_TYPES_FOR_AI,
  CREATE_DROPDOWN_OPTIONS,
  CREATE_DROPDOWN_OPTIONS_FOR_INLINE_IMAGE,
} from "AppComponents/GPTPlayground/modules/Utils";
import { generateRandomIdV2 } from "Utils";

const styles = {
  userAuthorTypeButtonStyles: { width: "116px" },
  messageTextareaStyles: {
    width: "100%",
    fontSize: "1.4rem",
    fontWeight: fontWeight["weight-500"],
    fieldSizing: "content",
  },
  assistantAuthorTypeButtonStyles: { width: "108px" },
  aiFeedbackContainerStyles: {
    borderRadius: "0px 0px 8px 8px",
    justifyContent: "flex-end",
  },
  inlineImage: "width: 128px; height: 128px; border-radius: 8px;", // It is used in HTML string.
};

const markdownComponents = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {_.first(children)}
    </a>
  ),
  annt: ClickableHighlightedText,
};

const getMessageActionButtons = props => {
  const {
    disabled = false,
    editMode,
    editHandler,
    saveHandler,
    deleteHandler,
    onCopyMessageClick,
    onSpeakMessageClick,
    messageId,
    isEmptyMessage,
    authorType,
    isAudioPlaying,
    isAudioGenerating,
    messageAudioPlayingForId,
  } = props;
  return (
    <div className={classes.messageAction}>
      {!editMode && authorType === "assistant" && !isEmptyMessage ? (
        <IconButton
          size={"small"}
          variant={
            messageAudioPlayingForId == messageId
              ? "destructive"
              : "outlined-subtle"
          }
          icon={
            messageAudioPlayingForId == messageId ? (
              <PauseFilled />
            ) : (
              <VolumeHighOutlined />
            )
          }
          disabled={
            disabled ||
            isAudioGenerating ||
            (isAudioPlaying && messageId !== messageAudioPlayingForId)
          }
          onClick={onSpeakMessageClick}
        />
      ) : null}
      {editMode ? (
        <IconButton
          size={"small"}
          variant={"progressive"}
          icon={<TickLargeOutlined />}
          disabled={disabled}
          onClick={saveHandler}
        />
      ) : (
        <IconButton
          size={"small"}
          variant={"outlined-subtle"}
          icon={<PencilLineOutlined />}
          disabled={disabled}
          onClick={editHandler}
        />
      )}
      {!editMode ? (
        <IconButton
          size={"small"}
          variant={"outlined-subtle"}
          icon={<CopyOutlined />}
          disabled={disabled}
          onClick={onCopyMessageClick}
        />
      ) : null}
      <IconButton
        size={"small"}
        variant={"outlined-subtle"}
        icon={<MinusCircleOutlined />}
        disabled={disabled}
        onClick={deleteHandler}
      />
    </div>
  );
};

const MessageAttachments = props => {
  const {
    node,
    attachments,
    mode = "view",
    onDeleteAttachmentClick = _.noop,
  } = props;
  return _.size(attachments) === 0 ? null : (
    <div className={classes.attachmentsContainer}>
      {_.map(attachments, attachment => (
        <MediaPlaceholder
          key={"AI_MESSAGE_" + node.id + "_" + attachment.id}
          attachments={[attachment]}
          showIconCardsWithExtension={true}
          showExtensionOnCornerByHover={true}
          showDeleteIconOnTopCorner={true}
          imageMaxWidth={200}
          imageMaxHeight={180}
          containerStyle={{
            maxWidth: "200px",
            minHeight: "180px",
          }}
          itemStyles={{
            borderRadius: "8px",
            width: "200px",
            height: "180px",
          }}
          mode={mode}
          deleteAttachment={onDeleteAttachmentClick}
          customDeleteButtonStyle={{ top: "12px" }}
        />
      ))}
    </div>
  );
};

const MessageComponent = ({
  aiConversationId,
  node,
  isPublicView,
  deleteAIMessage,
  updateAIMessage,
  editMessageId,
  setEditMessageId,
  setToastMsg,
  userId,
  isMessageAudioGenerating,
  messageAudioPlayingForId,
  isUploading,
  getSpeechToTextTranscript,
  createAIConversationAttachments,
  deleteAIConversationAttachment,
  handleSpeakClick,
}) => {
  {
    const { id, authorType, content, usageMetricsId } = node || {};

    const [messageContent, setMessageContent] = useState(content);
    const [isRecording, setIsRecording] = useState(false);
    const [isGeneratingTranscript, setIsGeneratingTranscript] = useState(false);
    const [attachments, setAttachments] = useState(
      _.get(node, "attachments", []) || []
    );
    const [showCodeEditor, setShowCodeEditor] = useState(false);
    const [showMathEditor, setShowMathEditor] = useState(false);
    const [code, setCode] = useState(_.get(node, "content"));
    const caretPosition = useRef({ start: 0, end: 0, selectedText: "" });
    const [latex, setLatex] = useState("");
    const [
      isMessageAttachmentDropdownVisible,
      setIsMessageAttachmentDropdownVisible,
    ] = useState(false);
    const [
      isInlineMessageAttachmentDropdownVisible,
      setIsInlineMessageAttachmentDropdownVisible,
    ] = useState(false);

    const onSpeakMessageClick = () => {
      handleSpeakClick({
        itemId: id,
        content,
        audioURL: _.get(node, "metadata.audioUrl", null),
      });
    };

    const editMode = _.isEqual(editMessageId, id) && !isPublicView;

    const textFieldRef = useRef(null);
    const audioRecorderNodeRef = useRef(null);

    const focusOnTextField = () => {
      if (textFieldRef?.current) {
        textFieldRef?.current?.focus();
        const textBoxTextlength = _.size(textFieldRef?.current?.value);
        textFieldRef?.current?.setSelectionRange(
          textBoxTextlength,
          textBoxTextlength
        );
      }
    };

    useEffect(() => {
      if (isPublicView) return;
      setMessageContent(content);
    }, [content]);

    useEffect(() => {
      if (isPublicView) return;
      if (editMode) {
        focusOnTextField();
        document.getElementById(`userMessageInputContainer-${id}`)?.focus?.();
      }
    }, [node, editMode]);

    const editHandler = () => {
      setEditMessageId(id);
      caretPosition.current = { start: 0, end: 0, selectedText: "" };
    };

    const saveHandler = async () => {
      setEditMessageId("");
      await updateAIMessage({
        aiConversationId,
        variables: {
          id,
          content: messageContent,
          metadata: _.omit(node.metadata, ["audioUrl"]),
        },
      });
    };

    const changeAuthorType = async () => {
      await updateAIMessage({
        aiConversationId,
        variables: {
          id,
          authorType: authorType === "user" ? "assistant" : "user",
          metadata: _.omit(node.metadata, ["audioUrl"]),
        },
      });
    };

    const deleteHandler = async () => {
      await deleteAIMessage({ aiConversationId, ids: [id] });
    };

    const handleOutClick = async e => {
      if (
        document
          .getElementById(`userMessageInputContainer-${id}`)
          ?.contains(e.relatedTarget) ||
        showMathEditor ||
        showCodeEditor ||
        isMessageAttachmentDropdownVisible ||
        isInlineMessageAttachmentDropdownVisible
      ) {
        return;
      }
      setEditMessageId("");
      await updateAIMessage({
        aiConversationId,
        variables: {
          id,
          content: messageContent,
          metadata: _.omit(node.metadata, ["audioUrl"]),
        },
      });
    };

    const handleUserMessageContentChange = value => {
      setMessageContent(value);
    };
    const handleAssistantMessageContentChange = e => {
      setMessageContent(e?.target?.value);
    };

    const onCopyMessageClick = () => {
      copyToClipboard(messageContent, () => {
        setToastMsg({
          msg: "toastMsgs:copied_capital",
          type: "success",
        });
      });
    };

    const onAudioAdded = async attachmentInput => {
      if (!_.isNil(attachmentInput)) {
        setIsGeneratingTranscript(true);
        const transcription = await getSpeechToTextTranscript({
          blobAttachment: attachmentInput,
          entityType: "USER",
          entityId: userId,
        });
        setMessageContent(oldContent =>
          _.trim(
            `${oldContent || ""} ${_.get(transcription, "transcript", "")}`
          )
        );
        setIsGeneratingTranscript(false);
        document.getElementById(`userMessageInputContainer-${id}`)?.focus?.();
      }
    };

    const onDeleteAttachmentClick = async attachment => {
      const updatedAttachments = _.filter(
        attachments,
        item => item.id !== attachment.id
      );
      setAttachments(updatedAttachments);
      await deleteAIConversationAttachment({
        input: { attachmentId: attachment?.id },
        aiConversationId,
      });
    };
    // add single or multiple attachments to attachmentItems state
    const addAttachment = async inputAttachments => {
      if (_.isEmpty(inputAttachments)) return;
      const newAttachments = _.castArray(inputAttachments).map(attachment => ({
        ...attachment,
        id: generateRandomIdV2(),
        parentId: node.id,
        parentType: "AI_MESSAGE",
      }));

      setAttachments(prevItems => [...prevItems, ...newAttachments]);
      await createAIConversationAttachments({
        input: newAttachments,
        aiConversationId,
      });
      setIsMessageAttachmentDropdownVisible(false);
    };

    const addInlineAttachment = async inputAttachments => {
      if (_.isEmpty(inputAttachments)) return;
      setIsInlineMessageAttachmentDropdownVisible(false);
      const newAttachments = _.castArray(inputAttachments).map(attachment => ({
        ...attachment,
        id: generateRandomIdV2(),
        parentId: node.id,
        parentType: "AI_MESSAGE",
      }));

      let imagesAsText = "";
      _.forEach(newAttachments, (attachment, index) => {
        imagesAsText +=
          (index === 0 ? "\n" : "") +
          `<img src="${attachment.url}" style="${styles.inlineImage}" id="${attachment.id}"/> ` +
          (index === _.size(newAttachments) - 1 ? "\n" : "");
      });

      setMessageContent(oldContent => {
        return `${oldContent}${imagesAsText}`;
      });
    };

    const onAddCodeClick = () => {
      try {
        const selection = window.getSelection();
        const selectedContent = _.get(selection, "baseNode.textContent", "");
        const start = selection?.baseOffset;
        const end = selection?.extentOffset;

        if (!_.isNil(start) && !_.isNil(end)) {
          const selectedText = selectedContent.substring(start, end);
          caretPosition.current = { start, end, selectedText };

          setCode(selectedText);
        }
        setShowCodeEditor(true);
      } catch (error) {}
    };

    const onAddMathClick = () => {
      try {
        const selection = window.getSelection();
        const selectedContent = _.get(selection, "baseNode.textContent", "");
        const start = selection?.baseOffset;
        const end = selection?.extentOffset;

        if (!_.isNil(start) && !_.isNil(end)) {
          const selectedText = _.replace(
            selectedContent.substring(start, end),
            "$",
            ""
          );
          caretPosition.current = { start, end, selectedText };

          setLatex(selectedText);
        }
        setShowMathEditor(true);
      } catch (error) {}
    };

    const onAddCodeHandler = code => {
      if (caretPosition.current?.start === caretPosition.current?.end) {
        setMessageContent(oldContent => {
          return `${oldContent}\n${code}`;
        });
        setShowCodeEditor(false);
      } else {
        const newContent = messageContent.replace(
          caretPosition.current?.selectedText,
          `${code}`
        );
        setMessageContent(newContent);
        setShowCodeEditor(false);
      }
      caretPosition.current = { start: 0, end: 0, selectedText: "" };
    };

    const onCloseCodeEditor = () => {
      setShowCodeEditor(false);
    };

    const handleInsertEquation = latexInput => {
      if (caretPosition.current?.start === caretPosition.current?.end) {
        setMessageContent(oldContent => {
          return `${oldContent} \n $$${latexInput.latex}$$`;
        });
        setShowMathEditor(false);
      } else {
        const newContent = messageContent.replace(
          caretPosition.current?.selectedText,
          `$$${latexInput.latex}$$`
        );
        setMessageContent(newContent);
        setShowMathEditor(false);
      }
      caretPosition.current = { start: 0, end: 0, selectedText: "" };
    };

    switch (authorType) {
      case "user": {
        return (
          <div key={id} className={classes.messageContainer}>
            <div className={classes.userMsg}>
              <Button
                size={"small"}
                variant={"outlined-subtle"}
                isFullWidth={true}
                style={styles.userAuthorTypeButtonStyles}
                onClick={changeAuthorType}
                disabled={_.includes(id, "NEW_") || isPublicView}
              >
                {"USER"}
              </Button>

              <div className={classes.messageUnitContainer}>
                {editMode ? (
                  <div ref={audioRecorderNodeRef} style={{ width: "100%" }}>
                    <div
                      className={classes.userMessageInputContainer}
                      style={{
                        display: isRecording ? "none" : "flex",
                      }}
                      id={`userMessageInputContainer-${id}`}
                      onBlur={handleOutClick}
                      role={"textbox"}
                      tabIndex={0}
                    >
                      {!isRecording && (
                        <>
                          <UIBaseContentEditable
                            placeholder={"Type your message here"}
                            value={messageContent}
                            customContainerClass={classes.userMessageTextArea}
                            disabled={isUploading || isGeneratingTranscript}
                            updateInputField={handleUserMessageContentChange}
                            allowEnter={true}
                            shouldPasteAsPlainText={true}
                            shouldParseInPlainText={false}
                          />
                          {isGeneratingTranscript && (
                            <SkeletonLoader height={"32px"} />
                          )}
                        </>
                      )}
                      {/* Attachments */}
                      <MessageAttachments
                        node={node}
                        attachments={attachments}
                        mode={"edit"}
                        onDeleteAttachmentClick={onDeleteAttachmentClick}
                      />
                      <div className={classes.inputOptionsContainer}>
                        <Tooltip tooltip={"Record voice ( Speech to Text )"}>
                          <div style={{ display: "flex" }}>
                            {/* Audio recording */}
                            <AudioRecorderSelector
                              parentType={"AI_MESSAGE"}
                              parentId={node?.id}
                              header={"Add voice message"}
                              version={"visualiser"}
                              onBlobAttachmentRecieved={onAudioAdded}
                              setChatbarVisible={value =>
                                setIsRecording(!value)
                              }
                              buttonComponent={
                                <IconButton
                                  size={"small"}
                                  icon={<MicrophoneOutlined />}
                                  variant="plain"
                                  disabled={
                                    isUploading ||
                                    isRecording ||
                                    isGeneratingTranscript
                                  }
                                />
                              }
                              container={audioRecorderNodeRef}
                              showValue={false}
                            />
                          </div>
                        </Tooltip>

                        {/* Upload File for entire message */}

                        <CreateDropDown
                          key={`upload_file_for_entire_message_${id}_${isMessageAttachmentDropdownVisible}`}
                          exportAsPdf={true}
                          parentId={aiConversationId}
                          parentType={"AI_CONVERSATION"}
                          accept={VALID_FILE_TYPES_FOR_AI}
                          mode={"edit"}
                          multiselect={true}
                          buttonComponent={
                            <Tooltip
                              tooltip={"Upload Image"}
                              key={`upload_file_for_entire_message_button_${id}`}
                            >
                              <IconButton
                                size={"small"}
                                icon={<UploadOutlined />}
                                variant="plain"
                                disabled={
                                  isUploading ||
                                  isRecording ||
                                  isGeneratingTranscript
                                }
                              />
                            </Tooltip>
                          }
                          dropDownOptions={CREATE_DROPDOWN_OPTIONS}
                          isNewDropdown={true}
                          onMultipleFilesUploaded={addAttachment}
                          disabled={isUploading}
                          dsVersion={"2.0"}
                          enableShareModal={false} // do not show DriveShareModal, directly add as a file
                          showDisplayText={false}
                          visible={isMessageAttachmentDropdownVisible}
                          onVisibleChange={
                            setIsMessageAttachmentDropdownVisible
                          }
                        />
                        {/* Upload File for inline message. (Image in between text) */}
                        <CreateDropDown
                          key={`upload_file_for_inline_message_${id}_${isInlineMessageAttachmentDropdownVisible}`}
                          parentId={aiConversationId}
                          parentType={"AI_CONVERSATION"}
                          accept={VALID_FILE_TYPES_FOR_AI}
                          mode={"edit"}
                          multiselect={true}
                          buttonComponent={
                            <Tooltip
                              tooltip={"Add inline image"}
                              key={`upload_file_for_inline_message_button_${id}`}
                            >
                              <IconButton
                                size={"small"}
                                icon={<ImageSquareOutlined />}
                                variant="plain"
                                disabled={
                                  isUploading ||
                                  isRecording ||
                                  isGeneratingTranscript
                                }
                              />
                            </Tooltip>
                          }
                          dropDownOptions={
                            CREATE_DROPDOWN_OPTIONS_FOR_INLINE_IMAGE
                          }
                          isNewDropdown={true}
                          onMultipleFilesUploaded={addInlineAttachment}
                          disabled={isUploading}
                          dsVersion={"2.0"}
                          enableShareModal={false} // do not show DriveShareModal, directly add as a file
                          showDisplayText={false}
                          visible={isInlineMessageAttachmentDropdownVisible}
                          onVisibleChange={
                            setIsInlineMessageAttachmentDropdownVisible
                          }
                        />

                        {/* Code Editor */}
                        <Tooltip tooltip={"Code"}>
                          <IconButton
                            size={"small"}
                            icon={<CodeOutlined />}
                            variant="plain"
                            disabled={
                              isUploading ||
                              isRecording ||
                              isGeneratingTranscript
                            }
                            onClick={onAddCodeClick}
                          />
                        </Tooltip>
                        {/* Math Editor */}
                        <Tooltip tooltip={"Math Equations"}>
                          <IconButton
                            size={"small"}
                            icon={<MathematicsOutlined />}
                            variant="plain"
                            disabled={
                              isUploading ||
                              isRecording ||
                              isGeneratingTranscript
                            }
                            onClick={onAddMathClick}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={classes.messageDetailsContainer}>
                    <div className={classes.singleMsg}>
                      {_.isEmpty(messageContent) ? (
                        "Edit this block to enter a user message..."
                      ) : (
                        <MarkdownView
                          content={_.replace(messageContent, /###/g, "-----")}
                          contentContainerClass={classes.blockContentText}
                          components={markdownComponents}
                          enableHTMLrendering={true}
                          enableHighlightedText={true}
                        />
                      )}
                      {/* Attachments */}
                    </div>
                    <MessageAttachments node={node} attachments={attachments} />
                  </div>
                )}
                {!isPublicView
                  ? getMessageActionButtons({
                      editMode,
                      editHandler,
                      saveHandler,
                      deleteHandler,
                      onCopyMessageClick,
                      authorType,
                      messageId: id,
                      isEmptyMessage: _.isEmpty(messageContent),
                      disabled:
                        _.includes(id, "NEW_") ||
                        isRecording ||
                        isUploading ||
                        isGeneratingTranscript,
                    })
                  : null}
              </div>
            </div>
            {showCodeEditor ? (
              <CodeEditor
                onCloseHandler={onCloseCodeEditor}
                onAddCodeHandler={onAddCodeHandler}
                input={code}
              />
            ) : null}
            {showMathEditor ? (
              <MathEquationEditor
                closeEditor={() => setShowMathEditor(false)}
                onInsertButtonClick={handleInsertEquation}
                latex={latex}
              />
            ) : null}
          </div>
        );
      }

      case "assistant": {
        const blockContentClass = classNames({
          [classes.blockContentText]: true,
        });

        const block = _.isEmpty(messageContent) ? (
          "Edit this block to enter an assistant message..."
        ) : (
          <MarkdownView
            content={_.replace(messageContent, /###/g, "-----")}
            contentContainerClass={blockContentClass}
            components={markdownComponents}
            enableHTMLrendering={true}
            enableHighlightedText={true}
          />
        );

        const blockClass = classNames({
          [classes.block]: true,
        });

        const blockWrapperClass = classNames({
          [classes.blockWrapper]: true,
        });

        return block ? (
          <div key={id} className={classes.assistantMsg}>
            <div className={classes.assistantMsgUpper}>
              <Button
                size={"small"}
                variant={"outlined-subtle"}
                isFullWidth={true}
                style={styles.assistantAuthorTypeButtonStyles}
                onClick={changeAuthorType}
                disabled={_.includes(id, "NEW_") || isPublicView}
              >
                {"ASSISTANT"}
              </Button>
            </div>

            <div className={classes.messageUnitContainer}>
              {editMode ? (
                <div className={classes.textAreaContainer}>
                  <TextAreaInput
                    ref={textFieldRef}
                    placeholder={"Enter an assistant message..."}
                    minRows={1}
                    value={messageContent}
                    onChange={handleAssistantMessageContentChange}
                    onBlur={handleOutClick}
                    style={styles.messageTextareaStyles}
                  />
                </div>
              ) : (
                <div className={blockWrapperClass}>
                  <div className={blockClass}>{block}</div>
                  {!isPublicView ? (
                    <AIFeedbackStrip
                      containerStyle={styles.aiFeedbackContainerStyles}
                      usageMetricsId={usageMetricsId}
                      showWarningText={false}
                    />
                  ) : null}
                </div>
              )}
              {!isPublicView
                ? getMessageActionButtons({
                    editMode,
                    editHandler,
                    saveHandler,
                    deleteHandler,
                    onCopyMessageClick,
                    onSpeakMessageClick,
                    messageId: id,
                    isEmptyMessage: _.isEmpty(messageContent),
                    isAudioPlaying: !_.isNil(messageAudioPlayingForId),
                    messageAudioPlayingForId,
                    isAudioGenerating: isMessageAudioGenerating,
                    authorType,
                    disabled:
                      _.includes(id, "NEW_") ||
                      isRecording ||
                      isUploading ||
                      isGeneratingTranscript,
                  })
                : null}
            </div>
          </div>
        ) : null;
      }
      default:
        return null;
    }
  }
};

MessageComponent.displayName = "MessageComponent";

const mapStateToProps = state => {
  const progressOfUploads = state.app_services.progressOfUploads;
  return {
    userId: getUserId(state),
    isUploading: !_.isEmpty(progressOfUploads),
  };
};

const mapActionCreators = {
  deleteAIMessage,
  updateAIMessage,
  setToastMsg,
  createAttachment,
  getSpeechToTextTranscript,
  createAIConversationAttachments,
  deleteAIConversationAttachment,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  MessageComponent
);
